import type {
  Availability,
  AvailabilityRange,
  SegmentState,
  TimelineSegment,
} from "../hooks/useHistory";
import { fmtDurationMs } from "../lib/format";

// Live up/down derived from the SSE stream, used to overlay the newest segment.
export interface LiveState {
  state: "up" | "down" | "unknown";
  t: number;
}

interface Props {
  data: Availability | null;
  range: AvailabilityRange;
  onRangeChange: (r: AvailabilityRange) => void;
  live?: LiveState | null;
}

const RANGES: AvailabilityRange[] = ["1h", "1d", "7d"];

function uptimeClass(pct: number): string {
  if (pct >= 99) return "uptime-ok";
  if (pct >= 95) return "uptime-warn";
  return "uptime-crit";
}

export function AvailabilityTimeline({
  data,
  range,
  onRangeChange,
  live,
}: Props) {
  const hasReal = !!(data && data.timeline?.length);

  // Segments to render. With real data we use it as-is; without it we render a
  // STABLE "no data yet" timeline instead of the old random preview that
  // reshuffled on every render and reported a fake uptime.
  const SEG_COUNT = 96;
  const SEG_MS = 15 * 60 * 1000;
  const baseT = live?.t ?? Date.now();
  const timeline: TimelineSegment[] = hasReal
    ? data!.timeline.map((s) => ({ ...s }))
    : Array.from({ length: SEG_COUNT }, (_, i) => ({
        t: baseT - (SEG_COUNT - i) * SEG_MS,
        state: "nodata" as SegmentState,
      }));

  // Live overlay: the newest segment reflects the live SSE status immediately,
  // so a device going down/up shows within seconds instead of waiting for the
  // history poll + 5-minute Influx bucket.
  if (live && live.state !== "unknown" && timeline.length > 0) {
    const last = timeline.length - 1;
    timeline[last] = { ...timeline[last], state: live.state };
  }

  const isMock = !hasReal;
  const displayData = {
    uptimePct: hasReal ? data!.uptimePct : live?.state === "up" ? 100 : 0,
    offlineEvents: hasReal ? data!.offlineEvents : 0,
    reboots: hasReal ? data!.reboots : 0,
    downtimeMs: hasReal ? data!.downtimeMs : 0,
    currentlyOffline: live
      ? live.state === "down"
      : hasReal
        ? data!.currentlyOffline
        : false,
    timeline,
  };

  const uptime = displayData.uptimePct;
  const downtime = Math.round((100 - uptime) * 10) / 10;

  // Chart dimensions for the merged up/down graph
  const chartW = 720;
  const chartH = 120;
  const padT = 10;
  const padB = 10;
  const plotH = chartH - padT - padB;

  const segCount = displayData.timeline.length;
  const segW = segCount > 0 ? chartW / segCount : chartW;
  const yUp = padT;             // GPU up   → line rides the top
  const yDown = padT + plotH;   // GPU down → line rides the bottom

  // One value per timeline segment — strict up/down, no averaging. "up" is
  // the only up state; "down", "partial" and "reboot" all read as down;
  // "nodata" segments are gaps (nothing is drawn).
  const cells = displayData.timeline.map((seg, i) => ({
    x0: i * segW,
    x1: (i + 1) * segW,
    up: seg.state === "up",
    gap: seg.state === "nodata",
  }));

  const flat = (c: { x0: number; x1: number }, y: number) =>
    `M${c.x0.toFixed(1)},${y.toFixed(1)} L${c.x1.toFixed(1)},${y.toFixed(1)}`;

  // Green fill sits under every "up" segment and collapses to nothing while
  // the GPU is down, so outages read as gaps in the green.
  const upArea = cells
    .filter((c) => !c.gap && c.up)
    .map(
      (c) =>
        `${flat(c, yUp)} L${c.x1.toFixed(1)},${chartH} L${c.x0.toFixed(1)},${chartH} Z`,
    )
    .join(" ");

  // Horizontal flats — green at the top where up, red at the bottom where down.
  const upLine = cells
    .filter((c) => !c.gap && c.up)
    .map((c) => flat(c, yUp))
    .join(" ");
  const downLine = cells
    .filter((c) => !c.gap && !c.up)
    .map((c) => flat(c, yDown))
    .join(" ");

  // Vertical connectors at every up↔down transition between adjacent segments.
  const verticals = cells
    .map((c, i) => {
      const n = cells[i + 1];
      if (c.gap || !n || n.gap || n.up === c.up) return "";
      return `M${c.x1.toFixed(1)},${yUp.toFixed(1)} L${c.x1.toFixed(1)},${yDown.toFixed(1)}`;
    })
    .filter(Boolean)
    .join(" ");

  const timeLabels = (() => {
    const now = Date.now();
    const labels: string[] = [];
    if (range === "1h") {
      // 13 labels: -60m, -55m, -50m, ..., now
      for (let i = 12; i >= 0; i--) {
        const d = new Date(now - i * 5 * 60 * 1000);
        labels.push(d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }));
      }
    } else if (range === "1d") {
      // 13 labels: -24h, -22h, -20h, ..., now
      for (let i = 12; i >= 0; i--) {
        const d = new Date(now - i * 2 * 60 * 60 * 1000);
        labels.push(d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }));
      }
    } else {
      // 7d: 8 labels, 1 per day (from 7 days ago to today)
      for (let i = 7; i >= 0; i--) {
        const d = new Date(now - i * 24 * 60 * 60 * 1000);
        labels.push(d.toLocaleDateString(undefined, { month: "short", day: "numeric" }));
      }
    }
    return labels;
  })();

  return (
    <div className="availability">
      <div className="availability-head">
        <span className="availability-title">Availability Timeline</span>
        <span className="range-toggle">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              className={`range-btn${r === range ? " range-btn-active" : ""}`}
              onClick={() => onRangeChange(r)}
            >
              {r}
            </button>
          ))}
        </span>
      </div>

      <div
        className="timeline"
        role="img"
        aria-label={`${displayData.uptimePct}% uptime over the last ${range}`}
      >
        {displayData.timeline.map((seg) => (
          <span
            key={seg.t}
            className={`timeline-seg timeline-${seg.state}`}
            title={`${new Date(seg.t).toLocaleString()} — ${seg.state}`}
          />
        ))}
      </div>
      <div className="availability-axis">
        {timeLabels.map((label, idx) => (
          <span key={idx}>{label}</span>
        ))}
      </div>

      {/* Single merged up/down graph — green flat = up, red flat = down */}
      {displayData.timeline.length >= 2 && (
        <div className="availability-graph-card graph-availability">
          <div className="graph-card-header">
            <span className="graph-card-title">Up / Down History</span>
            <span className="graph-legend">
              <span className="graph-legend-item val-up">● {uptime}% up</span>
              <span className="graph-legend-item val-down">● {downtime}% down</span>
            </span>
          </div>
          <div className="svg-wrapper">
            <svg
              viewBox={`0 0 ${chartW} ${chartH}`}
              width="100%"
              height={chartH}
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="upGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3bd278" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#2c9d54" stopOpacity="0.02" />
                </linearGradient>
              </defs>

              {/* level guides: up at the top, down at the bottom */}
              <line x1="0" y1={yUp} x2={chartW} y2={yUp} stroke="rgba(255,255,255,0.06)" strokeDasharray="4 4" />
              <line x1="0" y1={yDown} x2={chartW} y2={yDown} stroke="rgba(255,255,255,0.06)" strokeDasharray="4 4" />

              {/* green fill under every "up" segment */}
              <path d={upArea} fill="url(#upGrad)" />

              {/* vertical transitions between up and down */}
              <path d={verticals} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />

              {/* up flats (green, top) and down flats (red, bottom) */}
              <path d={upLine} fill="none" stroke="#3bd278" strokeWidth="2.5" strokeLinecap="butt" />
              <path d={downLine} fill="none" stroke="#ff6173" strokeWidth="2.5" strokeLinecap="butt" />
            </svg>
          </div>
          <div className="graph-card-footer">
            {timeLabels.map((label, idx) => (
              <span key={idx}>{label}</span>
            ))}
          </div>
        </div>
      )}

      <div className="availability-stats">
        <span className={`uptime-pct ${uptimeClass(displayData.uptimePct)}`}>
          {displayData.uptimePct}% uptime
        </span>
        <span className="dot-sep" aria-hidden="true">
          ·
        </span>
        <span>
          {displayData.offlineEvents}{" "}
          {displayData.offlineEvents === 1 ? "outage" : "outages"}
        </span>
        {displayData.downtimeMs > 0 ? (
          <>
            <span className="dot-sep" aria-hidden="true">
              ·
            </span>
            <span>{fmtDurationMs(displayData.downtimeMs)} down</span>
          </>
        ) : null}
        {displayData.reboots > 0 ? (
          <>
            <span className="dot-sep" aria-hidden="true">
              ·
            </span>
            <span>
              {displayData.reboots}{" "}
              {displayData.reboots === 1 ? "reboot" : "reboots"}
            </span>
          </>
        ) : null}
        {isMock && (
          <span className="availability-badge-preview">Awaiting data</span>
        )}
      </div>
    </div>
  );
}
